import { ReactElement, useState } from 'react'

import { addAlertSignature } from '../../actions/alerts'
import { NewPayRoll } from '../../api/pay-rolls'
import paths from '../../constants/paths'
import Company from '../../model/company'
import { DateFormat } from '../../model/types'
import { EmployeeReducer } from '../../reducers/employees'
import { SalaryCycleReducer } from '../../reducers/salaryCycles'
import { formatLoadingText } from '../../utils/loading-utils'
import { t, tx } from '../../utils/translation-utils'
import Button from '../elements/button'
import Card from '../elements/card'
import Modal from '../elements/modal'
import Title from '../elements/Title'
import jsBrowserHistory from '../widgets/jsBrowserHistory'
import LoadingOverlay from '../widgets/LoadingOverlay'
import PayRollsAddEmployeeList from './PayRollAddEmployeeList'

import './PayRollAdd.css'

type Props = {
  company: Company
  salaryCycles: SalaryCycleReducer
  employees: EmployeeReducer
  addAlert: addAlertSignature
  addPayRoll: (payRoll: NewPayRoll) => void
  startZeroTaxReport: (month: DateFormat) => void
}

export default function PayRollsAdd(props: Props): ReactElement | null {
  const [showModal, setShowModal] = useState<boolean>(false)

  const handleSubmit = (salaryCycleID: string, salaryPeriodID: string, type: string, employeeIDs: string[]) => {
    const salaryPeriod = props.salaryCycles.salaryCycles
      .find((salaryCycle) => salaryCycle.id === salaryCycleID)
      ?.salaryPeriods?.find((salaryPeriod) => salaryPeriod.id === salaryPeriodID)

    if (!salaryPeriod) {
      return
    }
    if (type === 'Extra') {
      props.addPayRoll({
        companyID: props.company.id,
        type: 'Extra',
        salaryPeriodID: salaryPeriod.id,
        dispositionDate: salaryPeriod.dispositionDate,
        employeeIDs: employeeIDs,
        paySlipIDs: [],
      })
    } else if (type === 'ZeroTaxReport') {
      props.startZeroTaxReport(salaryPeriod.start)
    }
    props.addAlert('success', t('pay_rolls.add.alert.success'), { timeout: 5 })
    jsBrowserHistory.push('/' + paths.PAY_ROLLS)
  }

  if (!props.employees.loaded || !props.salaryCycles.loaded) {
    return (
      <LoadingOverlay
        text={formatLoadingText([
          { loading: !props.employees.loaded, text: t('loading.reducer.employees') },
          { loading: !props.salaryCycles.loaded, text: t('loading.reducer.salary_cycles') },
        ])}
      />
    )
  }

  return (
    <>
      <Card className="pay-roll-create">
        <div className="title-and-back-btn">
          <Title>{t('pay_rolls.add.title')}</Title>
          <Button type="secondary" onClick={() => setShowModal(true)}>
            {t('pay_rolls.manual.button.cancel')}
          </Button>
        </div>
        <p>{t('pay_rolls.add.intro')}</p>
        <p>
          {tx('pay_rolls.add.help_article', {
            link: (
              <a
                href="https://help.salary.dk/da/articles/5203842-opret-ekstraordinaer-lon-korsel"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('pay_rolls.add.help_article.link_text')}
              </a>
            ),
          })}
        </p>
        <PayRollsAddEmployeeList
          company={props.company}
          salaryCycles={props.salaryCycles.salaryCycles}
          employees={props.employees.employees}
          addAlert={props.addAlert}
          doManual={handleSubmit}
          addPayRoll={props.addPayRoll}
        />
      </Card>
      <Modal visible={showModal} footer={false} onCancel={() => setShowModal(false)}>
        <div className="pay-roll-cancel-modal">
          <Title>{t('pay_rolls.manual.popup_cancel_title')}</Title>
          <Button
            size="extra-extra-large"
            type="primary"
            onClick={() => {
              jsBrowserHistory.push('/' + paths.PAY_ROLLS)
              setShowModal(false)
            }}
          >
            {t('pay_rolls.manual.button.cancel')}
          </Button>
        </div>
      </Modal>
    </>
  )
}
